<script setup lang="ts">
import { NuxtLink } from '#components';
import type { Component, HTMLAttributes } from 'vue';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const props = withDefaults(defineProps<{
    label: string;
    value: string | number;
    description?: string;
    meta?: string;
    icon?: Component | null;
    href?: string;
    interactive?: boolean;
    active?: boolean;
    density?: 'default' | 'compact';
    class?: HTMLAttributes['class'];
}>(), {
    description: '',
    meta: '',
    icon: null,
    href: '',
    interactive: false,
    active: false,
    density: 'default',
    class: '',
});

const emit = defineEmits<{
    activate: [];
}>();

function handleKeydown(event: KeyboardEvent) {
    if (!props.interactive || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    emit('activate');
}
</script>

<template>
    <NuxtLink
        v-if="href"
        :to="href"
        class="block min-w-0"
        :aria-current="active ? 'page' : undefined"
    >
        <Card
            :class="cn(
                'metric-card min-w-0 rounded-xl py-0 transition-colors hover:bg-accent',
                active && 'border-foreground/25 bg-accent/40 ring-1 ring-foreground/10',
                props.class,
            )"
            :data-density="density"
        >
            <CardContent class="metric-card__content flex items-start justify-between gap-3 sm:gap-4">
                <div :class="density === 'compact' ? 'min-w-0 space-y-1' : 'min-w-0 space-y-2'">
                    <p :class="density === 'compact' ? 'text-xs text-muted-foreground' : 'text-sm text-muted-foreground'">{{ label }}</p>
                    <p :class="cn('metric-card__value break-words', density === 'compact' ? 'text-xl font-semibold tracking-tight text-foreground [font-family:var(--font-display)]' : 'text-2xl font-semibold tracking-tight text-foreground [font-family:var(--font-display)]')">{{ value }}</p>
                    <p v-if="description" class="text-xs text-muted-foreground">{{ description }}</p>
                </div>

                <div class="flex shrink-0 items-center gap-2 text-muted-foreground">
                    <span v-if="meta" class="text-xs font-medium">{{ meta }}</span>
                    <slot name="icon">
                        <component v-if="icon" :is="icon" class="size-5" />
                    </slot>
                </div>
            </CardContent>
        </Card>
    </NuxtLink>

    <Card
        v-else
        :class="cn(
            'metric-card min-w-0 rounded-xl py-0',
            interactive && 'cursor-pointer transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            props.class,
        )"
        :data-density="density"
        :role="interactive ? 'button' : undefined"
        :tabindex="interactive ? 0 : undefined"
        @click="interactive && emit('activate')"
        @keydown="handleKeydown"
    >
        <CardContent class="metric-card__content flex items-start justify-between gap-3 sm:gap-4">
            <div :class="density === 'compact' ? 'min-w-0 space-y-1' : 'min-w-0 space-y-2'">
                <p :class="density === 'compact' ? 'text-xs text-muted-foreground' : 'text-sm text-muted-foreground'">{{ label }}</p>
                <p :class="cn('metric-card__value break-words', density === 'compact' ? 'text-xl font-semibold tracking-tight text-foreground [font-family:var(--font-display)]' : 'text-2xl font-semibold tracking-tight text-foreground [font-family:var(--font-display)]')">{{ value }}</p>
                <p v-if="description" class="text-xs text-muted-foreground">{{ description }}</p>
            </div>

            <div v-if="meta || icon || $slots.icon" class="flex shrink-0 items-center gap-2 text-muted-foreground">
                <span v-if="meta" class="text-xs font-medium">{{ meta }}</span>
                <slot name="icon">
                    <component v-if="icon" :is="icon" class="size-5" />
                </slot>
            </div>
        </CardContent>
    </Card>
</template>
